import { useState } from "react";
import {
  CircularProgress,
  FormControlLabel,
  Grid2,
  Radio,
  RadioGroup,
} from "@mui/material";
// styles
import styles from "./PlanDetailsCardStyle.module.scss";
// local imports
import { ReactComponent as LaunchPad } from "../AdminAssets/svg/LaunchPad.svg";
import { ReactComponent as GrowthGear } from "../AdminAssets/svg/GrowthGear.svg";
import { ReactComponent as Momentum } from "../AdminAssets/svg/Momentum.svg";
import { ReactComponent as Booster } from "../AdminAssets/svg/Booster.svg";
import { useSelector } from "react-redux";
import { useEffect } from "react";
import { useMakeApiCalls } from "../../../Hooks/useMakeApiCalls";
import { Actions } from "../../../redux/constants/actionTypes";
import { getStore } from "../../../Utils/storeUtils";
import { useParams } from "react-router-dom";

const imageSize = {
  height: 30,
  width: 35,
};

const planImages = {
  LaunchPad: <LaunchPad {...imageSize} />,
  GrowthGear: <GrowthGear {...imageSize} />,
  Momentum: <Momentum {...imageSize} />,
  Booster: <Booster {...imageSize} />,
};

export default function PlanDetailsCard(props) {
  const {
    selectedPlan,
    setSelectedPlan,
    numOfSites,
    selectedOrg,
    planType,
    setPlanType,
    toggleType,
    setToggleType,
    setPriceId,
  } = props;

  const { orgId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [planError, setPlanError] = useState(true);
  // Accessing state using useSelector
  let licenseInfo = useSelector(
    (state) => state.plansInfo?.allPlans?.plans.licenses
  );
  const isDedicated = useSelector(
    (state) => state.plansInfo?.allPlans?.plans.dedicatedPlans
  );
  const dedicatedHosting = useSelector(
    (state) => state.plansInfo?.specificPlan?.isDedicated
  );

  const dedicatedPlans = dedicatedHosting === true ? isDedicated : licenseInfo;

  const userDetails = useSelector((state) => state.users?.userDetails);

  useMakeApiCalls(() => {
    setIsLoading(true);
    getStore().dispatch({
      type: Actions.API_GET_CHILD_PLAN_DETAILS,
      data: {
        orgId: selectedOrg?.orgId,
      },
      meta: {
        onSuccess: () => {
          setIsLoading(false);
        },
        onError: () => {
          setIsLoading(false);
        },
      },
    });
  });

  const filteredLicenseInfo = dedicatedPlans
    ?.filter((license) => license.licenseType !== "To The Moon")
    ?.sort((a, b) => {
      const order = ["LaunchPad", "GrowthGear", "Momentum", "Booster"];
      return order.indexOf(a.licenseType) - order.indexOf(b.licenseType);
    });

  const mappedPlans = filteredLicenseInfo?.map((license) => {
    const features = license.features?.reduce((acc, feature) => {
      acc[feature.type] = feature.limitOrAvailablity;
      return acc;
    }, {});

    const pricing = license.pricing?.reduce((acc, price) => {
      acc[price.billingType.toLowerCase()] = {
        priceInUSD: price.originalprice,
        priceInINR: price.orginalPrice_INR,
        priceId: price.stripePriceId,
        salePriceInr: price.salePriceInr,
      };
      return acc;
    }, {});
    return {
      planName: license.licenseType,
      planImage: planImages[license.licenseType] || null,
      numSites: features.NUM_SITES || "N/A",
      visits: features.VISITS || "N/A",
      storage: features.STORAGE ? `${features.STORAGE} GB` : "N/A",
      bindwidth: features.BANDWIDTH ? `${features.BANDWIDTH} GB` : "N/A",
      pricing: pricing,
      singleSitePlan: license.isSingleSitePlan,
      planId: license.planId,
    };
  });

  const getPlanErrorMessage = (planName, numOfSites) => {
    switch (planName) {
      case "LaunchPad":
        return `You are not eligible to select the LaunchPad as you already have ${numOfSites} websites.`;
      case "GrowthGear":
        return `You are not eligible to select the GrowthGear as you already have ${numOfSites} websites.`;
      case "Momentum":
        return `You are not eligible to select the Momentum as you already have ${numOfSites} websites.`;
      case "Booster":
        return `Connect with our Rocon team for a customized website plan.`;
      default:
        return "Please select a valid plan.";
    }
  };

  const handlePlanError = (plan) => {
    if (numOfSites > parseInt(plan.numSites, 10)) {
      setSelectedPlan(null);
      setPlanError({
        planName: plan.planName,
        errorMessage: getPlanErrorMessage(plan.planName, numOfSites),
      });
    }
  };
  const handlePlanClick = (plan) => {
    setPlanError(false);
    setSelectedPlan(plan);
  };

  const filteredPlans = mappedPlans?.filter((plan) => {
    if (planType === "solo") return plan.singleSitePlan === true;
    if (planType === "multi") return plan.singleSitePlan === false;
    return true;
  });

  if (selectedPlan) {
    const price = selectedPlan?.pricing?.[toggleType]?.priceId;
    setPriceId(price);
  }

  useMakeApiCalls(() => {
    getStore().dispatch({
      type: Actions.API_SELECTED_ORG,
      orgId: orgId,
    });
  });

  useEffect(() => {
    if (
      filteredPlans?.some((plan) => numOfSites >= parseInt(plan.numSites, 10))
    ) {
      setPlanType("multi");
    }
  }, [numOfSites]);

  const handleToggleChange = () => {
    setToggleType((prevType) =>
      prevType === "monthly" ? "yearly" : "monthly"
    );
  };

  return (
    <>
      <Grid2 className={styles.planName}>
        {dedicatedHosting === true ? "Dedicated Hosting" : "Kubernetes Hosting"}{" "}
        Plans
      </Grid2>
      <Grid2 className={styles.durationToggleContainer}>
        <Grid2 className={styles.monthColor}>Monthly</Grid2>
        <Grid2 className={styles.durationToggle} onClick={handleToggleChange}>
          <Grid2 className={styles.toggleOnOff}>
            <Grid2
              className={`${styles.monthlyLabel} ${
                toggleType === "monthly" ? styles.active : ""
              }`}
            />
            <Grid2
              className={`${
                toggleType === "monthly" ? styles.month : styles.year
              }`}
            />
            <Grid2
              className={`${styles.yearlyLabel} ${
                toggleType === "yearly" ? styles.active : ""
              }`}
            />
          </Grid2>
        </Grid2>
        <Grid2 container>
          <Grid2 className={styles.monthColor}>Yearly</Grid2>
          {toggleType === "monthly" ? (
            ""
          ) : (
            <Grid2 className={styles.discountTxt}>SAVE UPTO 85%</Grid2>
          )}
        </Grid2>
      </Grid2>
      <Grid2 className={styles.toggleContainer}>
        <Grid2 className={styles.containerRename}>
          <RadioGroup
            row
            value={planType}
            onChange={(e) => {
              setPlanType(e.target.value);
              setSelectedPlan(null);
            }}
          >
            <FormControlLabel
              value="multi"
              control={<Radio className={styles.radioButton} />}
              label="Multi-Site Plans"
              className={`${planType === "multi" ? styles.selected : ""}`}
              style={{
                color: planType === "multi" ? "#135fc2" : "inherit",
                cursor: "pointer",
              }}
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "14px",
                  fontWeight: "500",
                  color:
                    planType === "multi"
                      ? "#135fc2"
                      : userDetails?.theme === "dark"
                      ? "#ffffff"
                      : "#000000",
                },
                "& .MuiRadio-root": {
                  color:
                    planType === "multi"
                      ? "#135fc2"
                      : userDetails?.theme === "dark"
                      ? "#ffffff"
                      : "#000000",
                },
              }}
            />
            {/* <FormControlLabel
              value="solo"
              control={<Radio className={styles.radioButton} />}
              label="Solo-Site Plans"
              className={`${planType === "solo" ? styles.selected : ""}`}
              style={{
                color: planType === "solo" ? "#135fc2" : "inherit",
                cursor: "pointer",
              }}
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "14px",
                  fontWeight: "500",
                  color:
                    planType === "solo"
                      ? "#135fc2"
                      : userDetails?.theme === "dark"
                      ? "#ffffff"
                      : "#000000",
                },
                "& .MuiRadio-root": {
                  color:
                    planType === "solo"
                      ? "#135fc2"
                      : userDetails?.theme === "dark"
                      ? "#ffffff"
                      : "#000000",
                },
              }}
            /> */}
          </RadioGroup>
        </Grid2>
      </Grid2>

      {planType === "solo" &&
        filteredPlans?.some(
          (plan) => numOfSites >= parseInt(plan.numSites, 10)
        ) && (
          <Grid2 className={styles.errorNote}>
            You are eligible for a Multi-Site Plan, as you have more than one
            site.
          </Grid2>
        )}

      {planType === "multi" &&
        filteredPlans?.some(
          (plan) => numOfSites > parseInt(plan.numSites, 10)
        ) && (
          <>
            {filteredPlans.map(
              (plan) =>
                numOfSites > parseInt(plan.numSites, 10) &&
                planError?.planName === plan.planName && (
                  <Grid2 key={plan.planId} className={styles.errorNote}>
                    {planError?.errorMessage}
                  </Grid2>
                )
            )}
          </>
        )}

      {/* Plan Details */}
      <Grid2 className={styles.planDetailsCardContainer}>
        {isLoading ? (
          <CircularProgress className={styles.loading} />
        ) : (
          <>
            <Grid2 className={styles.sitesBox}>
              <Grid2 className={styles.sites}>No.Of SITES</Grid2>
              <Grid2 className={styles.sites}>VISITS</Grid2>
              <Grid2 className={styles.sites}>STORAGE</Grid2>
              <Grid2 className={styles.sites}>BANDWIDTH</Grid2>
            </Grid2>

            <Grid2 className={styles.CardContainer}>
              {filteredPlans?.map((plan, index) => (
                <Grid2
                  item
                  key={index}
                  className={`${styles.planCard} ${
                    selectedPlan?.planId === plan.planId &&
                    numOfSites <= parseInt(plan.numSites)
                      ? styles.selected
                      : ""
                  } ${
                    numOfSites > parseInt(plan.numSites)
                      ? styles.planCardBlur
                      : ""
                  }`}
                  onClick={() => {
                    if (numOfSites <= parseInt(plan.numSites)) {
                      handlePlanClick(plan);
                    } else handlePlanError(plan);
                  }}
                >
                  <Grid2 className={styles.imagePlan}>
                    <Grid2>{plan.planImage}</Grid2>
                    <Grid2 className={styles.planName}>{plan.planName}</Grid2>
                  </Grid2>
                  <Grid2 className={styles.price}>
                    {toggleType === "monthly" ? (
                      <>
                        {selectedOrg?.country === "USA" ? (
                          <div>{`$${plan.pricing.monthly.priceInUSD}/M`}</div>
                        ) : (
                          <div>{`₹${plan.pricing.monthly.priceInINR}/M`}</div>
                        )}
                      </>
                    ) : (
                      <>
                        {selectedOrg?.country === "USA" ? (
                          <div>{`$${(
                            plan.pricing.yearly.priceInUSD / 12
                          ).toFixed(2)}/M`}</div>
                        ) : (
                          <div>{`₹${(
                            plan.pricing.yearly.priceInINR / 12
                          ).toFixed(2)}/M`}</div>
                        )}
                      </>
                    )}
                  </Grid2>
                  <Grid2 className={styles.numSites}>{plan.numSites}</Grid2>
                  <Grid2 className={styles.numSites}>{plan.visits}</Grid2>
                  <Grid2 className={styles.numSites}>{plan.storage}</Grid2>
                  <Grid2 className={styles.numSites}>{plan.bindwidth}</Grid2>
                </Grid2>
              ))}
            </Grid2>
          </>
        )}
      </Grid2>

      <Grid2 className={styles.customPlanTxt}>
        <Grid2>
          <span className={styles.noteLabel}>Want a custom plan?</span>
          <span
            className={styles.contactBtn}
            onClick={() => {
              window.open("https://roconpaas.com/contact-us/", "_blank");
            }}
          >
            Contact Us
          </span>
        </Grid2>
        <Grid2 />
        <Grid2>
          <span
            className={styles.contactBtn}
            onClick={() => {
              window.open(
                "https://roconpaas.com/managed-wordpress-hosting-pricing/",
                "_blank"
              );
            }}
          >
            Explore More
          </span>
        </Grid2>
      </Grid2>
    </>
  );
}
